import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  CreditCard as CreditIcon,
  CardMembership as MembershipIcon,
  AttachMoney as MoneyIcon,
  EventAvailable as EventIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import JuntoApiService, { User } from '../services/api';
import { logger } from '../utils/logger';

interface UserWallet {
  id: string;
  user_id: string;
  cash_balance: number;
  credits: UserCredit[];
  memberships: UserMembership[];
}

interface UserCredit {
  id: string;
  credit_id: string;
  credit_name: string;
  quantity: number;
  expiry_date: string;
}

interface UserMembership {
  id: string;
  membership_id: string;
  membership_name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserWalletPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userWallet, setUserWallet] = useState<UserWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [walletLoading, setWalletLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadUserWallet(selectedUserId);
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getUsers();
      setUsers(data);
      setError(null);
      
      // Auto-select first user if available
      if (data.length > 0) {
        setSelectedUserId(data[0].id);
      }
    } catch (err) {
      logger.error('Failed to load users', err as Error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadUserWallet = async (userId: string) => {
    try {
      setWalletLoading(true);
      const wallet = await JuntoApiService.getUserWallet(userId);
      // Map API response to local interface
      const mappedWallet: UserWallet = {
        id: userId,
        user_id: wallet.user_id,
        cash_balance: wallet.balance || 0,
        credits: (wallet.credits || []).map(c => ({
          id: c.credit_id,
          credit_id: c.credit_id,
          credit_name: c.credit_name,
          quantity: c.quantity || 0,
          expiry_date: c.expires_at || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        })),
        memberships: (wallet.memberships || []).map(m => ({
          id: m.membership_id,
          membership_id: m.membership_id,
          membership_name: m.membership_name,
          start_date: new Date().toISOString(),
          end_date: m.expires_at,
          is_active: m.expires_at ? new Date(m.expires_at) > new Date() : false
        }))
      };
      setUserWallet(mappedWallet);
      setError(null);
    } catch (err) {
      logger.error('Failed to load user wallet', err as Error);
      setUserWallet(null);
      setError('Failed to load user wallet');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSelectedUser = (): User | undefined => {
    return users.find(user => user.id === selectedUserId);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };


  const isExpiringSoon = (expiryDate: string): boolean => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
    return daysUntilExpiry <= 7;
  };

  const isExpired = (expiryDate: string): boolean => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  const selectedUser = getSelectedUser();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Wallet Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* User Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <PersonIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Select User</Typography>
        </Box>
        
        <FormControl fullWidth>
          <InputLabel>User</InputLabel>
          <Select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            label="User"
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Box>
                  <Typography variant="body1">{user.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email} • Joined {formatDate(user.created_at)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedUser && (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Phone:</strong> {selectedUser.phone || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Emergency Contact:</strong> {selectedUser.emergency_contact_name || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Wallet Information */}
      {selectedUserId && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                icon={<WalletIcon />}
                label="Wallet"
                iconPosition="start"
              />
              <Tab
                icon={<HistoryIcon />}
                label="History"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {walletLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>Loading wallet...</Typography>
            </Box>
          ) : (
            <>
              {/* Wallet Tab */}
              <TabPanel value={tabValue} index={0}>
                {userWallet ? (
                  <Grid container spacing={3}>
                    {/* Cash Balance */}
                    <Grid item xs={12} md={4}>
                      <Card>
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={2}>
                            <MoneyIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="h6">Cash Balance</Typography>
                          </Box>
                          <Typography variant="h4" color="success.main">
                            ${(userWallet.cash_balance || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Available for purchases
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Credits Summary */}
                    <Grid item xs={12} md={4}>
                      <Card>
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={2}>
                            <CreditIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Credits</Typography>
                          </Box>
                          <Typography variant="h4">
                            {userWallet.credits.reduce((sum, credit) => sum + credit.quantity, 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total available credits
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Memberships Summary */}
                    <Grid item xs={12} md={4}>
                      <Card>
                        <CardContent>
                          <Box display="flex" alignItems="center" mb={2}>
                            <MembershipIcon color="secondary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Memberships</Typography>
                          </Box>
                          <Typography variant="h4">
                            {userWallet.memberships.filter(m => m.is_active).length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Active memberships
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Credits Detail */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Credit Details
                      </Typography>
                      {userWallet.credits.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Credit Type</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Expiry Date</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {userWallet.credits.map((credit) => (
                                <TableRow key={credit.id}>
                                  <TableCell>{credit.credit_name}</TableCell>
                                  <TableCell>{credit.quantity}</TableCell>
                                  <TableCell>{formatDate(credit.expiry_date)}</TableCell>
                                  <TableCell>
                                    {isExpired(credit.expiry_date) ? (
                                      <Chip label="Expired" color="error" size="small" />
                                    ) : isExpiringSoon(credit.expiry_date) ? (
                                      <Chip label="Expiring Soon" color="warning" size="small" />
                                    ) : (
                                      <Chip label="Active" color="success" size="small" />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No credits available
                        </Typography>
                      )}
                    </Grid>

                    {/* Memberships Detail */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Membership Details
                      </Typography>
                      {userWallet.memberships.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Membership Type</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {userWallet.memberships.map((membership) => (
                                <TableRow key={membership.id}>
                                  <TableCell>{membership.membership_name}</TableCell>
                                  <TableCell>{formatDate(membership.start_date)}</TableCell>
                                  <TableCell>{formatDate(membership.end_date)}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={membership.is_active ? "Active" : "Inactive"} 
                                      color={membership.is_active ? "success" : "default"} 
                                      size="small" 
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No memberships found
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No wallet found for this user
                  </Typography>
                )}
              </TabPanel>

              {/* History Tab */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <CalendarIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="h6">Member Since</Typography>
                        </Box>
                        <Typography variant="h5">
                          {selectedUser ? formatDate(selectedUser.created_at) : 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUser && new Date(selectedUser.created_at) ? 
                            `${Math.floor((new Date().getTime() - new Date(selectedUser.created_at).getTime()) / (1000 * 3600 * 24))} days ago`
                            : ''
                          }
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <EventIcon color="secondary" sx={{ mr: 1 }} />
                          <Typography variant="h6">Classes Taken</Typography>
                        </Box>
                        <Typography variant="h5">
                          Coming Soon
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Class history tracking
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Recent Activity
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Class booking history and credit usage tracking will be implemented here.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default UserWalletPage;